import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Camera, ChevronLeft, ChevronRight, X, Expand } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import OptimizedImage from '@/components/OptimizedImage';

interface DetailPhotoGalleryProps {
  photos: string[];
  title?: string;
  className?: string;
}

export const DetailPhotoGallery: React.FC<DetailPhotoGalleryProps> = ({
  photos,
  title = 'Photo Gallery',
  className
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  if (!photos || photos.length === 0) return null;

  const nextImage = () => setCurrentIndex((prev) => (prev + 1) % photos.length);
  const prevImage = () => setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);

  return (
    <>
      <Card className={cn("border-border", className)}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Camera className="w-4 h-4 text-primary" />
            {title}
            <span className="ml-auto text-xs text-muted-foreground font-normal">
              {photos.length} photo{photos.length !== 1 ? 's' : ''}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Main Image */}
          <div 
            className="relative aspect-video rounded-lg overflow-hidden bg-muted mb-2 cursor-pointer"
            onClick={() => setFullscreen(true)}
          >
            <OptimizedImage
              src={photos[currentIndex]}
              alt={`Photo ${currentIndex + 1}`}
              className="w-full h-full object-contain"
              size="full"
            />
            
            {/* Navigation Overlay */}
            {photos.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white h-8 w-8 rounded-full"
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white h-8 w-8 rounded-full"
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </>
            )}
            
            {/* Counter */}
            <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
              {currentIndex + 1} / {photos.length}
            </div>
            
            {/* Expand Button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white h-8 w-8 rounded-full"
              onClick={() => setFullscreen(true)}
            >
              <Expand className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Thumbnails */}
          {photos.length > 1 && (
            <div className="flex gap-1.5 overflow-x-auto scrollbar-hide py-1">
              {photos.map((photo, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  aria-label={`Select photo ${idx + 1}`}
                  className={cn(
                    "flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    idx === currentIndex 
                      ? "border-primary ring-2 ring-primary/20" 
                      : "border-transparent hover:border-muted-foreground/30"
                  )}
                >
                  <OptimizedImage 
                    src={photo} 
                    alt={`Thumb ${idx + 1}`} 
                    className="w-full h-full object-cover" 
                    size="thumbnail" 
                  />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Fullscreen Modal */}
      <Dialog open={fullscreen} onOpenChange={setFullscreen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-none">
          <div className="relative w-full h-[90vh] flex items-center justify-center">
            <OptimizedImage
              src={photos[currentIndex]}
              alt={`Photo ${currentIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              size="full"
            />
            
            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white h-10 w-10 rounded-full"
              onClick={() => setFullscreen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
            
            {/* Navigation */}
            {photos.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white h-12 w-12 rounded-full"
                  onClick={prevImage}
                >
                  <ChevronLeft className="w-6 h-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white h-12 w-12 rounded-full"
                  onClick={nextImage}
                >
                  <ChevronRight className="w-6 h-6" />
                </Button>
              </>
            )}
            
            {/* Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/10 text-white text-sm px-4 py-2 rounded-full">
              {currentIndex + 1} / {photos.length}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DetailPhotoGallery;
